// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { WarningImage } from '../../icons/status';
import { useAppStyles } from '../../styles';
import { TrackContext } from '../common';
import { CancelButton, ConfirmButton, ConfirmationField } from '../dialogs';
import CloseButton from '../dialogs/close-button';

// Unpublish activity confirmation dialog properties.
export type UnpublishActivityDialogProps = Readonly<
    Partial<DialogProps> & {
        title: string;
        onUnpublish: () => void;
    }
>;

// Unpublish activity confirmation dialog.
export default (props: UnpublishActivityDialogProps) => {
    const { title, onUnpublish, open } = props;
    const [confirmed, setConfirmed] = useState(false);
    const styles = useAppStyles();

    return (
        <TrackContext.Provider value={{ container: 'unpublish-activity-dialog' }}>
            <Dialog {...props}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle action={<CloseButton />}>
                            <FormattedMessage
                                description="Unpublish activity dialog title."
                                defaultMessage="Unpublish activity"
                            />
                        </DialogTitle>
                        <DialogContent className={styles.flexRowCentered}>
                            <WarningImage className={styles.dialogImage} />
                            <div className={styles.dialogContent}>
                                <div>
                                    <FormattedMessage
                                        description="Confirmation text on unpublish activity dialog."
                                        defaultMessage="Are you sure you want to unpublish activity ''{ACTIVITY_TITLE}''?"
                                        values={{ ACTIVITY_TITLE: title }}
                                    />
                                </div>
                                <div>
                                    <FormattedMessage
                                        description="Warning message on the unpublish activity dialog."
                                        defaultMessage="All student progress associated with the activity will be deleted."
                                    />
                                </div>
                                <ConfirmationField
                                    dialogOpen={open}
                                    onChange={setConfirmed}
                                    confirmationText="UNPUBLISH"
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton className={styles.buttonDanger} onClick={onUnpublish} disabled={!confirmed}>
                                <FormattedMessage
                                    description="Unpublish activity dialog confirmation button text."
                                    defaultMessage="Unpublish activity"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
};
